import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import productPageProduct from './ProductPage';
// import { trackFilter } from '../utils/analytics';
import config from '../config';

const product = productPageProduct.product || {
  id: 1,
  name: 'Void',
  price: 0.5,
  images: [
    '/void1.jpg',
    '/void2.jpg',
    '/void3.jpg',
  ],
};

const UPLOADS_BASE = config.UPLOADS_BASE || config.API_BASE;
const getUrl = img =>
  img && img.startsWith('/uploads/')
    ? `${UPLOADS_BASE}${img}`
    : img && !img.startsWith('http')
      ? `${UPLOADS_BASE}/uploads/${img.replace(/^\/+/,'')}`
      : img;

const imgStyle = {
  width: 262,
  height: 262,
  objectFit: 'cover',
  marginBottom: 12,
  transition: 'transform 0.2s ease',
};

function normalizeListProduct(p) {
  let images = p.images;
  if (typeof images === 'string') {
    try {
      images = JSON.parse(images);
    } catch {
      images = images ? [images] : [];
    }
  }
  if (!Array.isArray(images)) images = images ? [images] : [];

  let categories = [];
  if (p.categories && Array.isArray(p.categories)) {
    categories = p.categories;
  } else if (p.category && typeof p.category === 'string') {
    try {
      const parsed = JSON.parse(p.category);
      categories = Array.isArray(parsed) ? parsed : [p.category];
    } catch {
      categories = [p.category];
    }
  } else if (p.category && Array.isArray(p.category)) {
    categories = p.category;
  }

  categories = categories
    .filter((c) => c && c !== 'undefined' && c !== 'null')
    .map((c) => {
      if (typeof c === 'object' && c !== null) {
        return c.name || c.title || c.label || String(c);
      }
      return String(c);
    });

  return { ...p, images, categories };
}

export default function ProductList({ onAddToCart }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  const productsApi = `${config.API_BASE}/api/products`;
  const cacheKey = 'cachedProducts';
  const cacheTimeKey = 'productsCacheTime';

  // Function to fetch categories from admin API
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${config.API_BASE}/api/categories`);
      const data = await res.json();
      if (data.success && data.categories.length > 0) {
        const adminCategories = data.categories.map(c => c.name);
        setCategories(['All', ...adminCategories]);
        console.log('✅ Categories refreshed from admin:', adminCategories);
      } else {
        setCategories(['All']);
        console.log('⚠️  No categories found in admin');
      }
    } catch (error) {
      console.error('❌ Failed to fetch categories:', error);
      setCategories(['All']);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const cachedProducts = localStorage.getItem(cacheKey);
    const cacheTime = localStorage.getItem(cacheTimeKey);
    const now = Date.now();
    const cacheAge = now - (cacheTime ? parseInt(cacheTime, 10) : 0);

    let hadValidCache = false;
    if (cachedProducts && cacheAge < 5 * 60 * 1000) {
      try {
        const parsedProducts = JSON.parse(cachedProducts);
        setProducts(parsedProducts);
        setLoading(false);
        hadValidCache = true;
      } catch {
        // ignore corrupt cache
      }
    }

    if (!hadValidCache) {
      setLoading(true);
    }

    const categoriesUrl = `${config.API_BASE}/api/categories`;

    Promise.all([
      fetch(productsApi).then((res) => res.json()),
      fetch(categoriesUrl).then((res) => res.json())
    ])
      .then(([data, catData]) => {
        if (cancelled) return;
        if (data.success) {
          const normalized = data.products.map(normalizeListProduct);
          localStorage.setItem(cacheKey, JSON.stringify(normalized));
          localStorage.setItem(cacheTimeKey, String(Date.now()));
          setProducts(normalized);
          if (catData.success && catData.categories?.length > 0) {
            setCategories(['All', ...catData.categories.map((c) => c.name)]);
          } else {
            setCategories(['All']);
          }
          setError('');
        } else {
          setError('Failed to fetch products');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to fetch products');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productsApi, cacheKey, cacheTimeKey]);

  // Refresh categories every 30 seconds to catch admin changes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCategories();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  if (loading && products.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#666'
      }}>
        <div>
          <div>Loading products...</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>
            {products.length > 0 ? 'Using cached data while fetching latest...' : 'Fetching from server...'}
          </div>
        </div>
      </div>
    );
  }
  
  if (error && products.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#666'
      }}>
        <div>
          <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              padding: '8px 16px', 
              background: '#000', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Filter products based on selected category
  const filteredProducts = selectedCategory === 'All' 
    ? products 
    : products.filter(product => {
        console.log(`Product: ${product.name}, Categories: "${product.categories}", Selected: "${selectedCategory}"`);
        return product.categories && product.categories.some(cat => 
          cat.toLowerCase() === selectedCategory.toLowerCase()
        );
      });

  const listOutOfStock = (p) => (p.total_inventory || 0) === 0;
  const sortedForDisplay = [...filteredProducts].sort((a, b) => {
    const aOut = listOutOfStock(a);
    const bOut = listOutOfStock(b);
    if (aOut === bOut) return 0;
    return aOut ? 1 : -1;
  });

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    // trackFilter('category', category);
  };

  console.log('Current categories:', categories);
  console.log('Selected category:', selectedCategory);
  console.log('Products count:', products.length);
  console.log('Filtered products count:', filteredProducts.length);
  console.log('All products with categories:', products.map(p => ({ name: p.name, categories: p.categories })));

  const homeFourColumnGrid = true;

  return (
    <div className="product-list">
      {/* Debug info - remove this later */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          background: '#f0f0f0', 
          padding: '10px', 
          margin: '10px', 
          fontSize: '12px',
          border: '1px solid #ccc'
        }}>
          <strong>Debug Info:</strong><br/>
          Selected: "{selectedCategory}"<br/>
          Total Products: {products.length}<br/>
          Filtered Products: {filteredProducts.length}<br/>
          Products with categories: {products.filter(p => p.categories && p.categories.length > 0).map(p => `${p.name}(${p.categories.join(', ')})`).join(', ')}
        </div>
      )}
      
      {/* Category Filter - Positioned in top left */}
      {categories.length > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-start', 
          alignItems: 'center', 
          gap: '12px', 
          marginBottom: '32px',
          marginTop: '20px',
          flexWrap: 'wrap',
          padding: '0 40px',
          maxWidth: '1600px',
          margin: '20px auto 32px auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginRight: '16px'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#333',
              marginRight: '8px'
            }}>
              Filter by:
            </span>
          </div>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => handleCategoryFilter(category)}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: selectedCategory === category ? '#000' : '#666',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: selectedCategory === category ? 'bold' : 'normal',
                textDecoration: 'underline',
                transition: 'all 0.2s ease',
                textTransform: 'capitalize'
              }}
              onMouseEnter={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.color = '#000';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.color = '#666';
                }
              }}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      <div
        className={homeFourColumnGrid ? 'product-list-grid-home' : undefined}
        style={
          homeFourColumnGrid
            ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: '1.5rem 1.25rem',
                alignItems: 'flex-start',
                width: '100%',
                maxWidth: '1600px',
                margin: '0 auto',
                padding: '0 40px',
                boxSizing: 'border-box',
              }
            : {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: '2rem',
                flexWrap: 'wrap',
              }
        }
      >
        {sortedForDisplay.map(product => {
          const isOutOfStock = listOutOfStock(product);
          return (
            <div 
              key={product.id} 
              style={{ 
                padding: 16, 
                borderRadius: 8, 
                minWidth: homeFourColumnGrid ? 0 : 180, 
                maxWidth: homeFourColumnGrid ? 'none' : 350, 
                flex: homeFourColumnGrid ? undefined : '1 1 262px', 
                width: homeFourColumnGrid ? '100%' : undefined,
                boxSizing: 'border-box', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                position: 'relative'
              }}
              title={isOutOfStock ? 'Out of stock' : ''}
            >
              <img
                src={getUrl(product.images && product.images[0] ? product.images[0] : '/void1.jpg')}
                alt={`${product.name} – unique clothing, jewelry, Bokeh`}
                loading="lazy"
                width="262"
                height="262"
                style={{ 
                  width: homeFourColumnGrid ? '100%' : 262, 
                  maxWidth: homeFourColumnGrid ? '100%' : undefined,
                  height: homeFourColumnGrid ? 'auto' : 262,
                  aspectRatio: homeFourColumnGrid ? '1 / 1' : undefined,
                  objectFit: 'cover', 
                  marginBottom: 12, 
                  transition: 'transform 0.2s ease', 
                  cursor: 'pointer',
                  opacity: isOutOfStock ? 0.6 : 1
                }}
                className="product-image"
                onClick={() => navigate(`/product/${product.slug || product.id}`)}
              />
              <h3 
                style={{ 
                  margin: 0, 
                  fontWeight: 'normal',
                  textDecoration: isOutOfStock ? 'line-through' : 'none',
                  color: isOutOfStock ? '#999' : 'inherit',
                  position: 'relative',
                  textAlign: 'center',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}
                className={isOutOfStock ? 'out-of-stock-product' : ''}
              >
                <span style={{ textAlign: 'center', width: '100%' }}>{product.name}</span>
                {isOutOfStock && (
                  <span 
                    style={{
                      fontSize: '12px',
                      fontWeight: 'normal',
                      color: '#999',
                      marginLeft: '8px',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      display: 'inline-block'
                    }}
                    className="out-of-stock-text"
                  >
                    Out of stock
                  </span>
                )}
              </h3>
              <p style={{ margin: 0 }}>${Number(product.price).toFixed(2)}</p>
            </div>
          );
        })}
        {filteredProducts.length === 0 && (
          <div style={{ 
            color: '#888', 
            textAlign: 'center', 
            padding: '40px 20px',
            width: '100%',
            gridColumn: homeFourColumnGrid ? '1 / -1' : undefined,
          }}>
            {selectedCategory === 'All' 
              ? 'No products found.' 
              : `No products found in "${selectedCategory}" category.`
            }
          </div>
        )}
      </div>
      <style>{`
        .product-image:hover {
          transform: scale(1.03);
        }
        .out-of-stock-product:hover .out-of-stock-text {
          opacity: 1 !important;
        }
        @media (max-width: 1100px) {
          .product-list-grid-home {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
          }
        }
        @media (max-width: 720px) {
          .product-list-grid-home {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            padding-left: 20px !important;
            padding-right: 20px !important;
          }
        }
        @media (max-width: 400px) {
          .product-list-grid-home {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
} 